import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useShippingPolicy } from '@/lib/hooks/useShippingPolicy';
import { trackEvent } from '@/lib/mixpanel';
import { ShippingPolicy } from '@/lib/types/shipping-policy';
import { cn, formatCurrency } from '@/lib/utils';
import { storeAtom } from '@/states/store.state';
import Button from '@appchoose/button';
import { useAtomValue } from 'jotai';
import { Edit } from 'lucide-react';

import {
  PickupPointDisplay,
  getNetworkLabel,
} from './pickup-point-configurator';

interface PickupPointBlockProps {
  dealId: string;
  hubspotStartDate?: Date | null;
  disabled?: boolean;
}

export function PickupPointBlock({
  dealId,
  disabled = false,
}: PickupPointBlockProps) {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const isOfferLocked = useIsOfferLocked();
  const { shippingPolicy, handleShippingPolicyChange } =
    useShippingPolicy(dealId);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canConfigure = !isOfferLocked && !disabled;

  const { pickupPointEnabled, pickupPointNetwork, pickupPointDeliveryPrice } =
    shippingPolicy;

  const openConfiguration = () => {
    if (!canConfigure) return;

    setConfigModalOpen(true);
    trackEvent('PICKUP_POINT_CONFIGURATION_OPENED', { dealId });
  };

  return (
    <>
      <section
        onClick={openConfiguration}
        className={cn(
          'flex items-center justify-between gap-4 rounded-lg border border-neutral-200 p-6 transition-colors',
          isOfferLocked
            ? 'cursor-not-allowed opacity-50'
            : !disabled
              ? 'cursor-pointer hover:bg-neutral-50'
              : ''
        )}
      >
        <div className="flex-1">
          <div className="mb-4 flex items-center gap-3">
            <h2 className="text-lg font-semibold">
              {pickupPointEnabled && pickupPointNetwork
                ? getNetworkLabel(pickupPointNetwork)
                : t('ui-v2.settings.blocks-shipping-policy.pickup-point.title')}
            </h2>
            <span
              className={cn(
                'rounded-full px-2.5 py-0.5 text-xs font-medium',
                pickupPointEnabled
                  ? 'bg-indigo-50 text-blue-900'
                  : 'bg-gray-100 text-gray-700'
              )}
            >
              {pickupPointEnabled
                ? t(
                    'ui-v2.settings.blocks-shipping-policy.pickup-point.status-requested'
                  )
                : t('ui-v2.settings.blocks-shipping-policy.status-inactive')}
            </span>
          </div>

          {pickupPointEnabled && (
            <div className="border-l border-l-gray-300 pl-4 text-sm text-muted-foreground">
              {pickupPointNetwork && pickupPointDeliveryPrice !== null && (
                <div>
                  {t(
                    'ui-v2.settings.blocks-shipping-policy.pickup-point.price'
                  )}{' '}
                  <span className="font-semibold text-foreground">
                    {pickupPointDeliveryPrice === 0
                      ? t(
                          'ui-v2.settings.blocks-shipping-policy.pickup-point.offered'
                        )
                      : formatCurrency(pickupPointDeliveryPrice, store)}
                  </span>
                </div>
              )}
            </div>
          )}

          {!pickupPointEnabled && (
            <p className="text-sm text-muted-foreground">
              {t(
                'ui-v2.settings.blocks-shipping-policy.pickup-point.no-policy'
              )}
            </p>
          )}
        </div>

        {!disabled && (
          <Button
            type="button"
            appearance="outline"
            containsIcon={true}
            onClick={(e) => {
              e.stopPropagation();
              openConfiguration();
            }}
            disabled={isOfferLocked}
            className="shrink-0 gap-2 border-0"
          >
            <Edit className="h-4 w-4" />
            {t('ui-v2.settings.blocks-shipping-policy.action-update')}
          </Button>
        )}
      </section>

      {configModalOpen && !disabled && (
        <PickupPointDisplay
          policy={shippingPolicy}
          onPolicyChange={async (newPolicy: ShippingPolicy) => {
            setIsSubmitting(true);
            try {
              const didSave = await handleShippingPolicyChange(newPolicy);
              if (!didSave) return;

              trackEvent('PICKUP_POINT_CONFIGURATION_SAVED', {
                dealId,
                enabled: newPolicy.pickupPointEnabled,
                networkConfigured: newPolicy.pickupPointNetwork !== null,
                deliveryPriceConfigured:
                  newPolicy.pickupPointDeliveryPrice !== null,
              });
              setConfigModalOpen(false);
            } finally {
              setIsSubmitting(false);
            }
          }}
          isLocked={isOfferLocked}
          isSubmitting={isSubmitting}
          open
          onOpenChange={setConfigModalOpen}
        />
      )}
    </>
  );
}
